import { useState, useEffect } from 'react'
import {
  Box,
  CircularProgress,
  Typography,
  Grid,
  Chip,
} from '@mui/material'
import { DataGridTable, TypoGraph } from 'components'
import { reportException } from 'tools/logs'
import moment, { Moment } from 'moment'
import { formatDateYYYYMMDD } from 'tools/helper'
import { ActiveVehicles } from 'interfaces/ISummaryReport'
import styles from './activeUnits.module.scss'
import strings from 'strings'
import { getActiveVehicles } from 'api'
import { getActiveUnitsColumns } from './activeUnitsTableData'

const t = strings.SUMMARY_REPORT.ACTIVE_UNITS

const ActiveUnits = () => {
  const carrierId = localStorage.getItem('carrierId') || '1'
  const [startDate, setStartDate] = useState<Moment | null>(null)
  const [endDate, setEndDate] = useState<Moment | null>(null)
  const [activeVehiclesData, setActiveVehiclesData] =
    useState<ActiveVehicles | null>(null)
  const [hasActiveVehicles, setHasActiveVehicles] = useState<boolean>(false)
  const [loadingActiveVehicles, setLoadingActiveVehicles] =
    useState<boolean>(false)


  useEffect(() => {
    const currentDate = moment().utc()
    const startDate = moment().utc().subtract(90, 'days')
    setStartDate(startDate)
    setEndDate(currentDate)
  }, [])

  useEffect(() => {
    if (startDate && endDate) {
      callGetActiveVehicles()
    }
  }, [startDate, endDate])

  const callGetActiveVehicles = async () => {
    if (!startDate || !endDate) return

    setLoadingActiveVehicles(true)
    try {
      const startDateFormatted = formatDateYYYYMMDD(startDate)
      const endDateFormatted = formatDateYYYYMMDD(endDate)

      const res = await getActiveVehicles(
        carrierId,
        startDateFormatted,
        endDateFormatted
      )

      if (res && res.vehicles && res.vehicles.length > 0) {
        setActiveVehiclesData(res)
        setHasActiveVehicles(true)
      } else {
        setActiveVehiclesData(null)
        setHasActiveVehicles(false)
      }
    } catch (error) {
      reportException(error)
      setHasActiveVehicles(false)
      setActiveVehiclesData(null)
    } finally {
      setLoadingActiveVehicles(false)
    }
  }

  const createPerformanceChip = (value: number | string, isPoor: boolean) => {
    const isEmpty = value === undefined || value === null || value === ''
    const displayValue = isEmpty ? t.EMPTY_VALUE : value

    const chipClass = !isEmpty
      ? isPoor
        ? styles.chipPoor
        : styles.chipGood
      : styles.chipEmpty

    return (
      <div className={styles.cellContainer}>
        <Chip
          label={displayValue}
          size="small"
          className={`${styles.performanceChip} ${chipClass}`}
        />
      </div>
    )
  }

  const ColorCoding = () => {
    return (
      <>
        <Box
          className={styles.colorCodingContainer}
        >
          <Box className={styles.colorCodingItem}>
            <Typography variant="caption" mr={2} color="text.secondary">
              {t.COLOR_CODING_TITLE}
            </Typography>

            <Box className={styles.colorCodingItem}>
              <Box className={`${styles.colorDot} ${styles.redDot}`} />
              <Typography variant="caption" color="text.secondary">
                {t.RED_LEGEND}
              </Typography>
            </Box>

            <Box className={styles.colorCodingItem}>
              <Box className={`${styles.colorDot} ${styles.grayDot}`} />
              <Typography variant="caption" color="text.secondary">
                {t.GRAY_LEGEND}
              </Typography>
            </Box>
          </Box>
        </Box>
      </>
    )
  }

  return (
    <Box className={styles.pageContainer}>
      <TypoGraph
        variant="h2"
        className={styles.title}
        mb={0}
        content={t.ACTIVE_UNITS_TITLE}
      />
      <ColorCoding />
      <Grid>
        {loadingActiveVehicles ? (
          <Box className={styles.loadingContainer}>
            <CircularProgress size={30} />
          </Box>
        ) : hasActiveVehicles && activeVehiclesData?.vehicles ? (
          <div className={styles.tableContainer}>
            <DataGridTable
              showTitle={false}
              showFilter={false}
              autoHeight
              id="activeVehiclesTable"
              loading={loadingActiveVehicles}
              data={activeVehiclesData.vehicles}
              columns={getActiveUnitsColumns(t, createPerformanceChip)}
              totalCount={activeVehiclesData.summary.totalVehicles}
              checkboxSelection={false}
              multiSearchFilter={true}
              getRowId={(row: any) => row.rowId}
              defaultPageSize={15}
              rowsPerPageOptions={[10, 15, 20]}
            />
          </div>
        ) : (
          <Box className={styles.noDataContainer}>
            <Typography variant="body1" color="text.secondary">
              {t.NO_DATA}
            </Typography>
          </Box>
        )}
      </Grid>
    </Box>
  )
}

export default ActiveUnits
